import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Page from './Page';
import PageWrapper from './PageWrapper';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { bare } from '@go-shared/lib/property';
import PropertyForm from '@go-client/components/BasicForms/PropertyForm';
import DDPForm from '@go-client/components/BasicForms/DDPForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './PropConfig.scss';
import {
  canViewDigitalDailyProgramForm,
  canViewNaveganteForm,
  canViewPropertyDetailsForm,
} from '@go-shared/lib/permissions';
import { BubbleAlert } from '@go-client/components/BubbleUI';
import ItineraryNavigation from '@go-client/components/Navigations/ItineraryNavigation';
import NaveganteForm from '@go-client/components/BasicForms/NaveganteForm';

interface PropConfigCategory {
  key: string;
  name: string;
}

interface PropConfigRouteParams {
  property: string;
}

type PropConfigPageProps = RouteComponentProps<PropConfigRouteParams>;

export default function PropConfig(props: PropConfigPageProps) {
  const { ...rest } = props;

  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const { activeProperty: property, refreshContext: refreshActiveProperty } = useActiveProperty();
  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsElem, setActionsElem] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setActionsElem(actionsRef.current);
  }, []);

  const categories: PropConfigCategory[] = [];

  if (canViewPropertyDetailsForm(user)) {
    categories.push({
      key: 'propManage',
      name: 'Property Details',
    });
  }

  if (canViewDigitalDailyProgramForm(user, serverInfo)) {
    categories.push({
      key: 'ddp',
      name: 'DDP',
    });
  }

  if (canViewNaveganteForm(user, serverInfo)) {
    categories.push({
      key: 'navegante-mobile',
      name: 'Mobile',
    });
  }

  const [activeCategory, setActiveCategory] = useState<PropConfigCategory | undefined>(
    categories[0]
  );

  return (
    <PageWrapper {...rest}>
      <Page className="PropConfig absolute flex items-stretch">
        <MenuBar {...rest} property={property} />
        <div className="SideMenu flex flex-col bg-navy-darkest shadow-md">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={property.brand.logo} />
            <PropertyBanner {...rest} property={property} swappable />
          </div>
        </div>
        <div className="PropConfigContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="PropConfigHeader align-with-logo flex items-end px-5 sticky top-0
            left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ItineraryNavigation active="property-config" />
            </div>
          </div>
          <div className="PropConfigBody mx-5 content-minus-logo min-w-900">
            {categories.length > 0 ? (
              <div className="flex flex-col">
                <div className="grid grid-cols-4 gap-4 items-start flex-grow">
                  <div className="bg-white shadow-md rounded overflow-hidden">
                    {categories.map(c => (
                      <div
                        key={c.key}
                        className={`list-item px-5 py-3 cursor-pointer ${
                          activeCategory && activeCategory.key === c.key ? 'active' : ''
                        }`}
                        onClick={() => setActiveCategory(c)}
                      >
                        <div className="flex items-center">
                          <div className="flex-grow min-w-0">
                            <div className="list-item-name font-semibold overflow-ellipsis">
                              {c.name}
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon="chevron-right"
                            className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {activeCategory && (
                    <div
                      className="sticky col-span-3"
                      style={{
                        top: '130px',
                        height: 'calc(100vh - 130px - 2rem - 1px)',
                      }}
                    >
                      <div className="bg-white shadow-md max-h-full flex flex-col rounded overflow-hidden">
                        <div
                          className="flex-grow overflow-auto"
                          style={{ maxHeight: 'calc(100% - 50px)' }}
                        >
                          <div style={{ marginBottom: '-1px' }}>
                            {activeCategory.key === 'ddp' && actionsElem && (
                              <div className="p-5 grid grid-cols-1 gap-4">
                                <DDPForm
                                  edit={property.settings}
                                  onFormSubmitted={refreshActiveProperty}
                                  actionsElem={actionsElem}
                                />
                              </div>
                            )}
                            {activeCategory.key === 'navegante-mobile' && actionsElem && (
                              <div className="p-5 grid grid-cols-1 gap-4">
                                <NaveganteForm
                                  edit={property.settings}
                                  onFormSubmitted={refreshActiveProperty}
                                  actionsElem={actionsElem}
                                />
                              </div>
                            )}
                            {activeCategory.key === 'propManage' && actionsElem && (
                              <div className="p-5 grid grid-cols-1 gap-4">
                                <PropertyForm
                                  edit={bare(property)}
                                  appearance="split"
                                  actionsElem={actionsElem}
                                  onFormSubmitted={refreshActiveProperty}
                                />
                              </div>
                            )}
                          </div>
                          <div
                            className="sticky bottom-0 p-3 bg-eggshell border-t flex"
                            ref={actionsRef}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3">&nbsp;</div>
              </div>
            ) : (
              <BubbleAlert variant="banana">No property configurations to show</BubbleAlert>
            )}
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
